import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GenericInput } from "../../../Shared/components/GenericInput";
import { GenericLink } from "../../../Shared/components/GenericLink";
import { GenericButton } from "../../../Shared/components/GenericButton";
import { LoginInput } from "../models/LoginInput";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { AuthService } from "../services/AuthService";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";

export const LoginForm = ({
  loginInput,
}: {
  loginInput: LoginInput | null;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const message = "Account Login Process, Please Wait";
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const [email, setEmail] = useState(loginInput?.email || "");
  const [emailError, setEmailError] = useState("");

  const [password, setPassword] = useState(loginInput?.password || "");
  const [passwordError, setPasswordError] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const onEmail = (val: string) => {
    setEmail(val);
    setEmailError(val.includes("@") ? "" : "");
  };

  const onPassword = (val: string) => {
    setPassword(val);
    setPasswordError(val?.length < 4 ? "" : "");
  };

  const resetForm = () => {
    setEmail("");
    setEmailError("");

    setPassword("");
    setPasswordError("");
  };

  const isFormValid =
    email.trim() !== "" &&
    password.trim() !== "" &&
    !emailError &&
    !passwordError;

  const loginAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      if (!email) setEmailError("Email is required");
      if (!password) setPasswordError("Password is required");
      return;
    } else {
      const loginInput = new LoginInput(email.trim(), password.trim());
      try {
        setIsLoading(true);
        await AuthService.login(loginInput);
        const isManagement = AuthService.isManagementAllowed();
        if (isManagement) {
          navigate("/services/accounts/home");
        } else {
          navigate("/services/accounts/companies");
        }
        resetForm();
        window.location.reload();
      } catch (error: any) {
        setShowToast(true);
        setToast({ message: `Error ${error?.message}`, type: "error" });
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(()=>{
    AuthService.removeLocalUser()
  },[])
  return (
    <>
      <div className="relative">
        <div className="w-full h-100">
          <h1 className="text-xl md:text-2xl font-bold leading-tight mt-12">
            {t('login')}
          </h1>
          <form className="mt-6" onSubmit={loginAction}>
            <GenericInput
              label={t('email_label')}
              value={email}
              onChange={onEmail}
              type="email"
              error={emailError}
              placeholder={t('email_hint')}
              required={true}
            />

            <GenericInput
              label={t('password_label')}
              value={password}
              onChange={onPassword}
              type="password"
              error={passwordError}
              placeholder={t('password_hint')}
              required={true}
            />

            <div className="text-right my-2 px-2">
              <GenericLink
                to="/auth/forgot-password"
                label={t('forgotPassword')}
              />
            </div>

            <GenericButton type="submit" disabled={!isFormValid} isFull={false}>
              {t('login')}
            </GenericButton>
          </form>

          <div className="my-6 border-gray-300 w-full"></div>

          <p className="mt-8 text-center">
            {t('signup_prompt_3')}{" "}
            <GenericLink to="/auth/register" label={t('signup_prompt_4')} />
          </p>
        </div>
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
